/**
 * CablePathDrawer
 *
 * Drawer component for viewing and editing cable path details.
 * Allows editing path type, route, conduit config, cables, and vertical drops.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { logger } from '@/lib/logger';
import { useAppStore, useSelectedCablePaths } from '@/stores/appStore';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { useQueryClient } from '@tanstack/react-query';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Separator } from '@/components/ui/separator';
import { X, Trash2, Plus, Cable, ArrowDown, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCablePathsStandardized } from '@/hooks/entities/useCablePathsStandardized';
import { useCableJunctionsStandardized } from '@/hooks/entities/useCableJunctionsStandardized';
import { useFloorPlanScale } from '@/hooks/useFloorPlanScale';
import type {
  CablePath,
  CableEntry,
  PathType,
  PathRoute,
  GenericCableType,
  ConduitSize,
  ConduitMaterial,
} from '@/types/cablePaths';
import {
  PATH_TYPE_LABELS,
  PATH_ROUTE_LABELS,
  GENERIC_CABLE_LABELS,
  CABLE_CATEGORIES,
  CONDUIT_SIZES,
  CONDUIT_MATERIAL_LABELS,
  DEFAULT_CABLE_ENTRY,
} from '@/types/cablePaths';

export function CablePathDrawer() {
  // Store state
  const activeDrawer = useAppStore(state => state.ui.activeDrawer);
  const setActiveDrawer = useAppStore(state => state.setActiveDrawer);
  const selectedCablePaths = useSelectedCablePaths();
  const selectedCableJunctions = useAppStore(state => state.selection.cableJunctions);
  const clearEntitySelection = useAppStore(state => state.clearEntitySelection);
  const clearAllSelections = useAppStore(state => state.clearAllSelections);

  // FloorPlan context
  const { floorPlan } = useFloorPlanContext();
  const queryClient = useQueryClient();

  // Get scale data
  const { scaleData, formatMeasurement } = useFloorPlanScale(floorPlan.id);

  // Get junctions and paths
  const { junctions, junctionCoordMap, deleteJunction } = useCableJunctionsStandardized(
    floorPlan.id,
  );
  const {
    paths: cablePaths,
    updatePath,
    deletePath,
    calculateLength,
  } = useCablePathsStandardized(floorPlan.id, junctionCoordMap, scaleData);

  // Local state
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Staged cables for bulk add (multi-selection mode)
  const [stagedCables, setStagedCables] = useState<Array<{ type: GenericCableType; qty: number }>>(
    [],
  );

  // Computed values - Check for multi-selection
  const pathCount = selectedCablePaths.size;
  const junctionCount = selectedCableJunctions.size;
  const totalSelected = pathCount + junctionCount;
  const isMultiSelection = totalSelected > 1;
  const isOpen = activeDrawer === 'cablePath' && (pathCount > 0 || junctionCount > 0);

  // Clear staged cables when multi-selection drawer opens
  useEffect(() => {
    if (isMultiSelection && isOpen) {
      setStagedCables([]);
    }
  }, [isMultiSelection, isOpen]);
  const selectedPathId =
    pathCount === 1 && junctionCount === 0 ? Array.from(selectedCablePaths)[0] : null;
  const selectedPath = selectedPathId ? cablePaths?.find(p => p.id === selectedPathId) : null;

  // Get all selected paths for bulk operations
  const selectedPathsList = cablePaths?.filter(p => selectedCablePaths.has(p.id)) || [];
  const selectedJunctionsList = junctions?.filter(j => selectedCableJunctions.has(j.id)) || [];

  // Get junction names for display
  const startJunction = selectedPath?.start_junction_id
    ? junctions?.find(j => j.id === selectedPath.start_junction_id)
    : null;
  const endJunction = selectedPath?.end_junction_id
    ? junctions?.find(j => j.id === selectedPath.end_junction_id)
    : null;

  // Calculate real-time length
  const pathLength = selectedPath ? calculateLength(selectedPath.id) : 0;

  // Handle close
  const handleClose = useCallback(() => {
    setActiveDrawer(null);
    clearEntitySelection('cablePath');
    clearEntitySelection('cableJunction');
  }, [setActiveDrawer, clearEntitySelection]);

  // Handle bulk delete (for multi-selection)
  const handleBulkDelete = async () => {
    setIsDeleting(true);
    try {
      // Delete all selected paths first
      for (const pathId of selectedCablePaths) {
        await deletePath(pathId);
      }
      // Then delete all selected junctions
      for (const junctionId of selectedCableJunctions) {
        await deleteJunction(junctionId);
      }
      toast.success(
        `Deleted ${pathCount} path${pathCount !== 1 ? 's' : ''} and ${junctionCount} junction${junctionCount !== 1 ? 's' : ''}`,
      );
      handleClose();
    } catch (error) {
      logger.error('Failed to bulk delete:', error);
      toast.error('Failed to delete some items');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle bulk update (update all selected paths with the same value)
  const handleBulkUpdate = async (updates: Partial<CablePath>) => {
    setIsUpdating(true);
    try {
      for (const pathId of selectedCablePaths) {
        await updatePath(pathId, updates);
      }
      toast.success(`Updated ${pathCount} path${pathCount !== 1 ? 's' : ''}`);
    } catch (error) {
      logger.error('Failed to bulk update:', error);
      toast.error('Failed to update some paths');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle bulk add cable (add a cable to all selected paths)
  const handleBulkAddCable = async (genericType: GenericCableType) => {
    setIsUpdating(true);
    try {
      for (const pathId of selectedCablePaths) {
        const path = cablePaths?.find(p => p.id === pathId);
        if (path) {
          const newCable: CableEntry = {
            ...DEFAULT_CABLE_ENTRY,
            generic_type: genericType,
            extra_unit: path.length_unit,
          };
          await updatePath(pathId, {
            cables: [...path.cables, newCable],
          });
        }
      }
      toast.success(`Added cable to ${pathCount} path${pathCount !== 1 ? 's' : ''}`);
    } catch (error) {
      logger.error('Failed to bulk add cable:', error);
      toast.error('Failed to add cable to some paths');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle path type change
  const handlePathTypeChange = async (value: PathType) => {
    if (!selectedPath) return;
    setIsUpdating(true);
    try {
      await updatePath(selectedPath.id, {
        path_type: value,
        // Clear conduit config if switching to cable-only
        conduit: value === 'cable_only' ? null : selectedPath.conduit,
      });
      toast.success('Path type updated');
    } catch (error) {
      logger.error('Failed to update path type:', error);
      toast.error('Failed to update path type');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle route change
  const handleRouteChange = async (value: PathRoute) => {
    if (!selectedPath) return;
    setIsUpdating(true);
    try {
      await updatePath(selectedPath.id, { route: value });
      toast.success('Route updated');
    } catch (error) {
      logger.error('Failed to update route:', error);
      toast.error('Failed to update route');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle conduit size change
  const handleConduitSizeChange = async (value: ConduitSize) => {
    if (!selectedPath) return;
    setIsUpdating(true);
    try {
      await updatePath(selectedPath.id, {
        conduit: {
          size: value,
          material: selectedPath.conduit?.material || 'EMT',
        },
      });
      toast.success('Conduit size updated');
    } catch (error) {
      logger.error('Failed to update conduit size:', error);
      toast.error('Failed to update conduit size');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle conduit material change
  const handleConduitMaterialChange = async (value: ConduitMaterial) => {
    if (!selectedPath) return;
    setIsUpdating(true);
    try {
      await updatePath(selectedPath.id, {
        conduit: {
          size: selectedPath.conduit?.size || '3/4',
          material: value,
        },
      });
      toast.success('Conduit material updated');
    } catch (error) {
      logger.error('Failed to update conduit material:', error);
      toast.error('Failed to update conduit material');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle vertical drop change
  const handleVerticalDropChange = async (which: 'start' | 'end', value: number) => {
    if (!selectedPath) return;
    setIsUpdating(true);
    try {
      const updates =
        which === 'start' ? { vertical_drop_start: value } : { vertical_drop_end: value };
      await updatePath(selectedPath.id, updates);
      toast.success('Vertical drop updated');
    } catch (error) {
      logger.error('Failed to update vertical drop:', error);
      toast.error('Failed to update vertical drop');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle add cable
  const handleAddCable = async () => {
    if (!selectedPath) return;
    setIsUpdating(true);
    try {
      const newCable: CableEntry = {
        ...DEFAULT_CABLE_ENTRY,
        extra_unit: selectedPath.length_unit,
      };
      await updatePath(selectedPath.id, {
        cables: [...selectedPath.cables, newCable],
      });
      toast.success('Cable added');
    } catch (error) {
      logger.error('Failed to add cable:', error);
      toast.error('Failed to add cable');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle remove cable
  const handleRemoveCable = async (index: number) => {
    if (!selectedPath) return;
    setIsUpdating(true);
    try {
      const newCables = [...selectedPath.cables];
      newCables.splice(index, 1);
      await updatePath(selectedPath.id, { cables: newCables });
      toast.success('Cable removed');
    } catch (error) {
      logger.error('Failed to remove cable:', error);
      toast.error('Failed to remove cable');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle cable type change
  const handleCableTypeChange = async (index: number, genericType: GenericCableType) => {
    if (!selectedPath) return;
    setIsUpdating(true);
    try {
      const newCables = [...selectedPath.cables];
      newCables[index] = {
        ...newCables[index],
        cable_ref_type: 'generic',
        generic_type: genericType,
        product_id: null,
      };
      await updatePath(selectedPath.id, { cables: newCables });
      toast.success('Cable type updated');
    } catch (error) {
      logger.error('Failed to update cable type:', error);
      toast.error('Failed to update cable type');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle cable quantity change
  const handleCableQtyChange = async (index: number, qty: number) => {
    if (!selectedPath) return;
    setIsUpdating(true);
    try {
      const newCables = [...selectedPath.cables];
      newCables[index] = { ...newCables[index], qty };
      await updatePath(selectedPath.id, { cables: newCables });
    } catch (error) {
      logger.error('Failed to update cable quantity:', error);
      toast.error('Failed to update cable quantity');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle delete path
  const handleDelete = async () => {
    if (!selectedPath) return;
    setIsDeleting(true);
    try {
      await deletePath(selectedPath.id);
      toast.success('Cable path deleted');
      handleClose();
    } catch (error) {
      logger.error('Failed to delete cable path:', error);
      toast.error('Failed to delete cable path');
    } finally {
      setIsDeleting(false);
    }
  };

  // Format length for display
  const formattedLength =
    pathLength > 0 && scaleData ? `${pathLength.toFixed(1)} ${scaleData.unit}` : 'N/A';

  // Don't render if not open or nothing selected
  if (!isOpen || totalSelected === 0) return null;

  // Multi-selection UI
  if (isMultiSelection) {
    return (
      <Sheet open={isOpen} modal={false} onOpenChange={open => !open && handleClose()}>
        <SheetContent
          side="right"
          className="w-[400px] overflow-y-auto [&>button]:hidden"
          onPointerDownOutside={e => {
            // Don't close if clicking on cable path edit handles or other cable elements
            // This allows multi-select with shift+click
            const target = e.target as HTMLElement | SVGElement;
            if (
              target?.closest?.('[data-handle-type]') ||
              target?.closest?.('[data-path-handles]') ||
              target?.closest?.('[data-edit-overlay]') ||
              target?.closest?.('[data-path-id]') ||
              target?.closest?.('[data-cable-layer]')
            ) {
              e.preventDefault();
              return;
            }
            e.preventDefault();
            handleClose();
          }}
          onInteractOutside={e => {
            // Don't close if interacting with cable path edit handles or other cable elements
            const target = e.target as HTMLElement | SVGElement;
            if (
              target?.closest?.('[data-handle-type]') ||
              target?.closest?.('[data-path-handles]') ||
              target?.closest?.('[data-edit-overlay]') ||
              target?.closest?.('[data-path-id]') ||
              target?.closest?.('[data-cable-layer]')
            ) {
              e.preventDefault();
              return;
            }
            e.preventDefault();
            handleClose();
          }}
        >
          <SheetHeader>
            <div className="flex items-center justify-between">
              <SheetTitle className="flex items-center gap-2">
                <Cable className="h-5 w-5" />
                Multiple Selected
              </SheetTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClose}
                className="h-8 w-8"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Selection Summary */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <div className="text-lg font-semibold">{totalSelected} items selected</div>
              <div className="text-sm text-muted-foreground">
                {pathCount > 0 && (
                  <span>
                    {pathCount} path{pathCount !== 1 ? 's' : ''}
                  </span>
                )}
                {pathCount > 0 && junctionCount > 0 && <span> • </span>}
                {junctionCount > 0 && (
                  <span>
                    {junctionCount} junction{junctionCount !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>

            {/* Bulk Actions for Paths */}
            {pathCount > 0 && (
              <>
                <Separator />
                <div className="space-y-4">
                  <div className="font-medium">Update All Paths</div>

                  {/* Bulk Path Type */}
                  <div className="space-y-2">
                    <Label>Path Type</Label>
                    <Select
                      onValueChange={v => handleBulkUpdate({ path_type: v as PathType })}
                      disabled={isUpdating}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Set for all..." />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(PATH_TYPE_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Bulk Route */}
                  <div className="space-y-2">
                    <Label>Route</Label>
                    <Select
                      onValueChange={v => handleBulkUpdate({ route: v as PathRoute })}
                      disabled={isUpdating}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Set for all..." />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(PATH_ROUTE_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Bulk Conduit */}
                  <div className="space-y-2">
                    <Label>Conduit Size</Label>
                    <Select
                      onValueChange={v =>
                        handleBulkUpdate({
                          path_type: 'conduit',
                          conduit: { size: v as ConduitSize, material: 'EMT' },
                        })
                      }
                      disabled={isUpdating}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Set for all..." />
                      </SelectTrigger>
                      <SelectContent>
                        {CONDUIT_SIZES.map(size => (
                          <SelectItem key={size} value={size}>
                            {size}"
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Bulk Add Cables Section */}
                  <Separator className="my-4" />
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Cable className="h-4 w-4" />
                        <span className="font-medium">Add Cables to All Paths</span>
                      </div>
                      <Select
                        onValueChange={v => {
                          setStagedCables(prev => [
                            ...prev,
                            { type: v as GenericCableType, qty: 1 },
                          ]);
                        }}
                        disabled={isUpdating}
                        value=""
                      >
                        <SelectTrigger className="w-[140px]">
                          <Plus className="h-3 w-3 mr-1" />
                          <span className="text-sm">Add cable</span>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectLabel>Data</SelectLabel>
                            {CABLE_CATEGORIES.data.map(type => (
                              <SelectItem key={type} value={type}>
                                {GENERIC_CABLE_LABELS[type]}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                          <SelectGroup>
                            <SelectLabel>Lighting Control</SelectLabel>
                            {CABLE_CATEGORIES.lighting.map(type => (
                              <SelectItem key={type} value={type}>
                                {GENERIC_CABLE_LABELS[type]}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                          <SelectGroup>
                            <SelectLabel>Power</SelectLabel>
                            {CABLE_CATEGORIES.power.map(type => (
                              <SelectItem key={type} value={type}>
                                {GENERIC_CABLE_LABELS[type]}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                          <SelectGroup>
                            <SelectLabel>Low Voltage</SelectLabel>
                            {CABLE_CATEGORIES.lowVoltage.map(type => (
                              <SelectItem key={type} value={type}>
                                {GENERIC_CABLE_LABELS[type]}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Staged Cables List */}
                    {stagedCables.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-2">
                        No cables staged. Add cables above.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {stagedCables.map((cable, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg"
                          >
                            <Select
                              value={cable.type}
                              onValueChange={v => {
                                setStagedCables(prev => {
                                  const updated = [...prev];
                                  updated[index] = {
                                    ...updated[index],
                                    type: v as GenericCableType,
                                  };
                                  return updated;
                                });
                              }}
                              disabled={isUpdating}
                            >
                              <SelectTrigger className="flex-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  <SelectLabel>Data</SelectLabel>
                                  {CABLE_CATEGORIES.data.map(type => (
                                    <SelectItem key={type} value={type}>
                                      {GENERIC_CABLE_LABELS[type]}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                                <SelectGroup>
                                  <SelectLabel>Lighting Control</SelectLabel>
                                  {CABLE_CATEGORIES.lighting.map(type => (
                                    <SelectItem key={type} value={type}>
                                      {GENERIC_CABLE_LABELS[type]}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                                <SelectGroup>
                                  <SelectLabel>Power</SelectLabel>
                                  {CABLE_CATEGORIES.power.map(type => (
                                    <SelectItem key={type} value={type}>
                                      {GENERIC_CABLE_LABELS[type]}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                                <SelectGroup>
                                  <SelectLabel>Low Voltage</SelectLabel>
                                  {CABLE_CATEGORIES.lowVoltage.map(type => (
                                    <SelectItem key={type} value={type}>
                                      {GENERIC_CABLE_LABELS[type]}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              </SelectContent>
                            </Select>

                            <Input
                              type="number"
                              min={1}
                              value={cable.qty}
                              onChange={e => {
                                setStagedCables(prev => {
                                  const updated = [...prev];
                                  updated[index] = {
                                    ...updated[index],
                                    qty: parseInt(e.target.value) || 1,
                                  };
                                  return updated;
                                });
                              }}
                              onKeyDown={e => e.stopPropagation()}
                              onKeyUp={e => e.stopPropagation()}
                              disabled={isUpdating}
                              className="w-16"
                            />

                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setStagedCables(prev => prev.filter((_, i) => i !== index));
                              }}
                              disabled={isUpdating}
                              className="h-8 w-8 text-destructive hover:text-destructive"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}

                        {/* Apply Staged Cables Button */}
                        <Button
                          onClick={async () => {
                            setIsUpdating(true);
                            try {
                              for (const pathId of selectedCablePaths) {
                                const path = cablePaths?.find(p => p.id === pathId);
                                if (path) {
                                  const newCables: CableEntry[] = stagedCables.map(staged => ({
                                    ...DEFAULT_CABLE_ENTRY,
                                    generic_type: staged.type,
                                    qty: staged.qty,
                                    extra_unit: path.length_unit,
                                  }));
                                  await updatePath(pathId, {
                                    cables: [...path.cables, ...newCables],
                                  });
                                }
                              }
                              toast.success(
                                `Added ${stagedCables.length} cable${stagedCables.length !== 1 ? 's' : ''} to ${pathCount} path${pathCount !== 1 ? 's' : ''}`,
                              );
                              setStagedCables([]);
                            } catch (error) {
                              logger.error('Failed to add cables:', error);
                              toast.error('Failed to add cables to some paths');
                            } finally {
                              setIsUpdating(false);
                            }
                          }}
                          disabled={isUpdating || stagedCables.length === 0}
                          className="w-full"
                        >
                          {isUpdating
                            ? 'Adding...'
                            : `Add ${stagedCables.length} Cable${stagedCables.length !== 1 ? 's' : ''} to ${pathCount} Path${pathCount !== 1 ? 's' : ''}`}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            <Separator />

            {/* Delete Button */}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="w-full" disabled={isDeleting}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete All Selected ({totalSelected})
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete {totalSelected} items?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete{' '}
                    {pathCount > 0 ? `${pathCount} path${pathCount !== 1 ? 's' : ''}` : ''}
                    {pathCount > 0 && junctionCount > 0 ? ' and ' : ''}
                    {junctionCount > 0
                      ? `${junctionCount} junction${junctionCount !== 1 ? 's' : ''}`
                      : ''}
                    . This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleBulkDelete} disabled={isDeleting}>
                    {isDeleting ? 'Deleting...' : 'Delete All'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Single path selection - show if no path selected
  if (!selectedPath) return null;

  return (
    <Sheet open={isOpen} modal={false} onOpenChange={open => !open && handleClose()}>
      <SheetContent
        side="right"
        className="w-[400px] overflow-y-auto [&>button]:hidden"
        onPointerDownOutside={e => {
          // Don't close if clicking on cable path edit handles or other cable elements
          // This allows multi-select with shift+click
          const target = e.target as HTMLElement | SVGElement;
          if (
            target?.closest?.('[data-handle-type]') ||
            target?.closest?.('[data-path-handles]') ||
            target?.closest?.('[data-edit-overlay]') ||
            target?.closest?.('[data-path-id]') ||
            target?.closest?.('[data-cable-layer]')
          ) {
            e.preventDefault();
            return;
          }
          e.preventDefault();
          handleClose();
        }}
        onInteractOutside={e => {
          // Don't close if interacting with cable path edit handles or other cable elements
          const target = e.target as HTMLElement | SVGElement;
          if (
            target?.closest?.('[data-handle-type]') ||
            target?.closest?.('[data-path-handles]') ||
            target?.closest?.('[data-edit-overlay]') ||
            target?.closest?.('[data-path-id]') ||
            target?.closest?.('[data-cable-layer]')
          ) {
            e.preventDefault();
            return;
          }
          e.preventDefault();
          handleClose();
        }}
      >
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2">
              <Cable className="h-5 w-5" />
              Cable Path
            </SheetTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="h-8 w-8"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Path Info */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Length</span>
              <Badge variant="secondary" className="text-base font-semibold">
                {formattedLength}
              </Badge>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">From</span>
              <span className="text-sm font-medium">{startJunction?.name || 'No junction'}</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">To</span>
              <span className="text-sm font-medium">{endJunction?.name || 'No junction'}</span>
            </div>
          </div>

          <Separator />

          {/* Path Type */}
          <div className="space-y-2">
            <Label>Path Type</Label>
            <Select
              value={selectedPath.path_type}
              onValueChange={v => handlePathTypeChange(v as PathType)}
              disabled={isUpdating}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PATH_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Route */}
          <div className="space-y-2">
            <Label>Route</Label>
            <Select
              value={selectedPath.route}
              onValueChange={v => handleRouteChange(v as PathRoute)}
              disabled={isUpdating}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PATH_ROUTE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Conduit Config - only show if path_type is conduit */}
          {selectedPath.path_type === 'conduit' && (
            <>
              <Separator />
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Settings2 className="h-4 w-4" />
                  <span className="font-medium">Conduit Settings</span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Size</Label>
                    <Select
                      value={selectedPath.conduit?.size || '3/4'}
                      onValueChange={v => handleConduitSizeChange(v as ConduitSize)}
                      disabled={isUpdating}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONDUIT_SIZES.map(size => (
                          <SelectItem key={size} value={size}>
                            {size}"
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Material</Label>
                    <Select
                      value={selectedPath.conduit?.material || 'EMT'}
                      onValueChange={v => handleConduitMaterialChange(v as ConduitMaterial)}
                      disabled={isUpdating}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(CONDUIT_MATERIAL_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </>
          )}

          <Separator />

          {/* Vertical Run */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <ArrowDown className="h-4 w-4" />
              <span className="font-medium">Vertical Run</span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Start ({startJunction?.name || 'A'})</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    step={0.5}
                    value={selectedPath.vertical_drop_start}
                    onChange={e =>
                      handleVerticalDropChange('start', parseFloat(e.target.value) || 0)
                    }
                    onKeyDown={e => e.stopPropagation()}
                    onKeyUp={e => e.stopPropagation()}
                    disabled={isUpdating}
                    className="w-20"
                  />
                  <span className="text-sm text-muted-foreground">{scaleData?.unit || 'ft'}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">End ({endJunction?.name || 'B'})</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    step={0.5}
                    value={selectedPath.vertical_drop_end}
                    onChange={e => handleVerticalDropChange('end', parseFloat(e.target.value) || 0)}
                    onKeyDown={e => e.stopPropagation()}
                    onKeyUp={e => e.stopPropagation()}
                    disabled={isUpdating}
                    className="w-20"
                  />
                  <span className="text-sm text-muted-foreground">{scaleData?.unit || 'ft'}</span>
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Cables */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Cable className="h-4 w-4" />
                <span className="font-medium">Cables</span>
              </div>
              <Button variant="outline" size="sm" onClick={handleAddCable} disabled={isUpdating}>
                <Plus className="h-3 w-3 mr-1" />
                Add
              </Button>
            </div>

            {selectedPath.cables.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No cables added yet</p>
            ) : (
              <div className="space-y-3">
                {selectedPath.cables.map((cable, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                    <Select
                      value={cable.generic_type || 'CAT6'}
                      onValueChange={v => handleCableTypeChange(index, v as GenericCableType)}
                      disabled={isUpdating}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel>Data</SelectLabel>
                          {CABLE_CATEGORIES.data.map(type => (
                            <SelectItem key={type} value={type}>
                              {GENERIC_CABLE_LABELS[type]}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                        <SelectGroup>
                          <SelectLabel>Lighting Control</SelectLabel>
                          {CABLE_CATEGORIES.lighting.map(type => (
                            <SelectItem key={type} value={type}>
                              {GENERIC_CABLE_LABELS[type]}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                        <SelectGroup>
                          <SelectLabel>Power</SelectLabel>
                          {CABLE_CATEGORIES.power.map(type => (
                            <SelectItem key={type} value={type}>
                              {GENERIC_CABLE_LABELS[type]}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                        <SelectGroup>
                          <SelectLabel>Low Voltage</SelectLabel>
                          {CABLE_CATEGORIES.lowVoltage.map(type => (
                            <SelectItem key={type} value={type}>
                              {GENERIC_CABLE_LABELS[type]}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>

                    <Input
                      type="number"
                      min={1}
                      value={cable.qty}
                      onChange={e => handleCableQtyChange(index, parseInt(e.target.value) || 1)}
                      onKeyDown={e => e.stopPropagation()}
                      onKeyUp={e => e.stopPropagation()}
                      disabled={isUpdating}
                      className="w-16"
                    />

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveCable(index)}
                      disabled={isUpdating}
                      className="h-8 w-8 text-destructive hover:text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Separator />

          {/* Delete Button */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="w-full" disabled={isDeleting}>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Path
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Cable Path?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete this cable path. The connected junction boxes will
                  remain but may become disconnected.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} disabled={isDeleting}>
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </SheetContent>
    </Sheet>
  );
}
